import type { ReactNode } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import type { SxProps } from '@mui/material/styles';
import type { AppShellAccount, UserMenuItem } from './types';
import { containerSx, contentGutter, fluidContainerSx, navBg, navHeight, topBarIconButtonSx } from './constants';
import { ContextBar } from './context-bar';
import { ShareLink } from './share-link';
import { UserMenu } from './user-menu';

type TopBarProps = {
  logo: ReactNode;
  account: AppShellAccount | null;
  userMenuGroups: UserMenuItem[][];
  logoutDisabled?: boolean;
  emitDocAttributes: boolean;
  onAiAssistantClick?: () => void;
  fluid?: boolean;
};

export function TopBar({
  logo,
  account,
  userMenuGroups,
  logoutDisabled = false,
  emitDocAttributes,
  onAiAssistantClick,
  fluid = false,
}: TopBarProps) {
  const aiDocProps = emitDocAttributes ? { 'data-doc-action': 'open-ai-assistant' } : {};
  let barContainerSx: SxProps = containerSx;
  if (fluid) {
    barContainerSx = fluidContainerSx;
  }

  return (
    <AppBar position="fixed" elevation={0} sx={{ bgcolor: navBg, zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar disableGutters sx={{ height: navHeight, minHeight: navHeight, p: 0 }}>
        <Box sx={[barContainerSx, { height: '100%' }]}>
          {/* No left gutter, so the logo starts on the rail's own edge; the right one is the page's,
              so the user menu ends where the page content ends. */}
          <Box sx={{ mr: contentGutter, display: 'flex', alignItems: 'center', gap: 3, height: '100%' }}>
            <Box
              component="a"
              href="/"
              sx={{ display: 'flex', alignItems: 'center' }}
              aria-label="Social Good Software home"
            >
              {logo}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
              <ContextBar />
            </Box>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <ShareLink account={account} emitDocAttributes={emitDocAttributes} />
              <IconButton
                aria-label="AI Assistant"
                title="AI Assistant"
                onClick={onAiAssistantClick}
                {...aiDocProps}
                sx={topBarIconButtonSx}
              >
                <AutoAwesomeOutlinedIcon aria-hidden />
              </IconButton>
              <UserMenu
                account={account}
                itemGroups={userMenuGroups}
                logoutDisabled={logoutDisabled}
                emitDocAttributes={emitDocAttributes}
              />
            </Box>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
